import React from "react";
//#region types
import "date-fns";
import { DateRange } from "models/formTypes/util";
import { PeriodisationMode } from "models/utilTypes/periodisationTypes";
import { TranslationKeys } from "translations";
import { IconDefinitionConstants } from "utils/constants";
//#endregion
//#region components
import DatePicker from "react-datepicker";
import { Box, ClickAwayListener, Grid, Popper, Tooltip } from "@mui/material";
import Icon from "components/common/icon";
import { ToggleSwitch } from "components/common";
//#endregion
import "react-datepicker/dist/react-datepicker.css";
import "theme/custom/react-datepicker.css";
import clsx from "clsx";
import cssExports from "theme/styles/main.module.scss";
import { DateHelper, LocalizationHelperInstance } from "utils/helpers";

export interface MonthOrDateRangePickerProps {
  mode: PeriodisationMode;
  onModeChange: (mode: PeriodisationMode) => void;
  range: DateRange | undefined;
  onRangeChange: (range: DateRange | undefined) => void;
  toggleLabel?: string;
  tooltipText?: string;
  minDate?: Date;
  maxDate?: Date;
  disabled?: boolean;
  modeToggleDisabled?: boolean;
  lockedTooltipText?: string;
  error?: boolean;
}

const MonthOrDateRangePicker: React.FC<MonthOrDateRangePickerProps> = (props) => {
  const {
    mode,
    onModeChange,
    range,
    onRangeChange,
    toggleLabel,
    tooltipText,
    minDate,
    maxDate,
    disabled,
    modeToggleDisabled,
    lockedTooltipText,
    error,
  } = props;

  const [isOpen, setIsOpen] = React.useState<boolean>(false);
  const [tooltipOpen, setTooltipOpen] = React.useState<boolean>(false);
  const [lockedTooltipOpen, setLockedTooltipOpen] = React.useState<boolean>(false);
  const [triggerEl, setTriggerEl] = React.useState<HTMLElement | null>(null);

  const formatStart = (date: Date): Date => {
    if (mode === "month") {
      const first = DateHelper.getFirstDayOfMonth(date);
      first.setHours(0, 0, 0, 0);
      return first;
    }
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
  };

  const formatEnd = (date: Date): Date =>
    DateHelper.getEndOfDay(mode === "month" ? DateHelper.getLastDayOfMonth(date) : date);

  const togglePopover = () => {
    if (!disabled) setIsOpen((prev) => !prev);
  };

  const closePopover = () => setIsOpen(false);

  // Capture phase, so an open picker takes Esc before a surrounding dialog closes on it.
  React.useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      closePopover();
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [isOpen]);

  const onPick = (newDate: Date | null) => {
    if (!newDate) return;

    if (mode === "month") {
      const pickedStart = formatStart(newDate);
      const pickedEnd = formatEnd(newDate);

      if (!range?.start || !range?.end) {
        onRangeChange({ start: pickedStart, end: pickedEnd });
        return;
      }

      const isSingleMonth =
        range.start.getFullYear() === range.end.getFullYear() && range.start.getMonth() === range.end.getMonth();

      if (isSingleMonth) {
        if (newDate < range.start) {
          onRangeChange({ start: pickedStart, end: range.end });
          closePopover();
        } else if (newDate > range.start) {
          onRangeChange({ start: range.start, end: pickedEnd });
          closePopover();
        }
        return;
      }

      onRangeChange({ start: pickedStart, end: pickedEnd });
      return;
    }

    let nextRange: DateRange;
    if (range?.end || !range?.start) {
      nextRange = { start: formatStart(newDate), end: undefined };
    } else if (range.start > newDate) {
      nextRange = { start: formatStart(newDate), end: formatEnd(range.start) };
    } else {
      nextRange = { ...range, end: formatEnd(newDate) };
    }
    onRangeChange(nextRange);
    if (nextRange.start && nextRange.end) closePopover();
  };

  const onToggleChange = (isDateMode: boolean) => {
    onModeChange(isDateMode ? "date" : "month");
  };

  const displayValue = range
    ? mode === "month"
      ? DateHelper.formatMonthRange(range.start ?? undefined, range.end ?? undefined)
      : DateHelper.formatDateRange(range.start ?? undefined, range.end ?? undefined)
    : "";

  const hasIncompleteRange = !!(range?.start && !range?.end) || !!(!range?.start && range?.end);

  const label = toggleLabel ?? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.PeriodiseWithDate);
  const tooltip =
    tooltipText ?? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.PeriodiseWithDateInfo);
  const lockedTooltip =
    lockedTooltipText ?? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.PeriodiseWithDateLocked);
  const incompleteRangeTooltip = LocalizationHelperInstance.getFormattedMessage(
    TranslationKeys.Common.MustSelectDateRange
  );
  const showIncompleteRangeTooltip = hasIncompleteRange && !isOpen;

  return (
    <ClickAwayListener onClickAway={closePopover}>
      <Box ref={setTriggerEl} className={cssExports.monthOrDateRangePickerWrapper}>
        <Tooltip
          title={incompleteRangeTooltip}
          arrow
          placement="right"
          open={showIncompleteRangeTooltip}
          classes={{
            tooltip: cssExports.monthOrDateRangePickerTooltipBubble,
            arrow: cssExports.monthOrDateRangePickerTooltipArrow,
            popper: cssExports.monthOrDateRangePickerTooltipPopper,
          }}
          PopperProps={{
            disablePortal: false,
            modifiers: [{ name: "offset", options: { offset: [0, 8] } }],
          }}
        >
          <Box
            className={clsx(
              cssExports.monthOrDateRangePickerTrigger,
              isOpen ? cssExports.datePickerOpen : "",
              disabled ? cssExports.disabledPicker : "",
              error ? cssExports.monthOrDateRangePickerTriggerError : ""
            )}
            onClick={togglePopover}
          >
            <Icon icon={IconDefinitionConstants.WCalendar} />
            <span
              className={clsx(
                cssExports.monthOrDateRangePickerTriggerValue,
                !displayValue ? cssExports.monthOrDateRangePickerTriggerPlaceholder : ""
              )}
            >
              {displayValue ||
                LocalizationHelperInstance.getFormattedMessage(
                  mode === "month" ? TranslationKeys.Common.SelectMonth : TranslationKeys.Common.SelectDates
                )}
            </span>
            {(range?.start || range?.end) &&
              (disabled ? (
                <span className={cssExports.monthOrDateRangePickerTriggerLock}>
                  <Icon icon={IconDefinitionConstants.WLock} />
                </span>
              ) : (
                <span
                  className={cssExports.monthOrDateRangePickerTriggerClear}
                  onClick={(e) => {
                    e.stopPropagation();
                    onRangeChange(undefined);
                  }}
                >
                  <Icon icon={IconDefinitionConstants.WTimesThin} />
                </span>
              ))}
          </Box>
        </Tooltip>

        <Popper
          open={isOpen}
          anchorEl={triggerEl}
          placement="bottom-start"
          disablePortal={false}
          modifiers={[{ name: "offset", options: { offset: [0, 4] } }]}
          className={cssExports.monthOrDateRangePickerPopperRoot}
        >
          <Box className={cssExports.monthOrDateRangePickerPopover}>
            <DatePicker
              inline
              selected={range?.start ?? undefined}
              startDate={range?.start ?? undefined}
              endDate={range?.end ?? undefined}
              minDate={minDate}
              maxDate={maxDate}
              showMonthYearPicker={mode === "month"}
              dateFormat={mode === "month" ? "MMM yyyy" : "yyyy-MM-dd"}
              formatWeekDay={(day) => day.charAt(0).toUpperCase()}
              {...({
                renderMonthContent: (_monthIndex: number, _shortMonth: string, longMonth: string) =>
                  longMonth.substring(0, 3),
              } as object)}
              onChange={onPick}
              allowSameDay
              disabledKeyboardNavigation
            />
            <Grid
              container
              item
              alignItems="center"
              justifyContent="space-between"
              wrap="nowrap"
              className={cssExports.monthOrDateRangePickerFooter}
            >
              <Grid item container alignItems="center" spacing={1} wrap="nowrap" xs>
                <Grid item>{label}</Grid>
                <Grid item>
                  <Tooltip
                    title={tooltip}
                    arrow
                    placement="bottom"
                    open={tooltipOpen}
                    onOpen={() => setTooltipOpen(true)}
                    onClose={() => setTooltipOpen(false)}
                    classes={{
                      tooltip: cssExports.monthOrDateRangePickerTooltipBubble,
                      arrow: cssExports.monthOrDateRangePickerTooltipArrow,
                      popper: cssExports.monthOrDateRangePickerTooltipPopper,
                    }}
                    PopperProps={{
                      disablePortal: false,
                      modifiers: [{ name: "offset", options: { offset: [0, 8] } }],
                    }}
                  >
                    <span
                      className={cssExports.monthOrDateRangePickerTooltipIcon}
                      onMouseEnter={() => setTooltipOpen(true)}
                      onMouseLeave={() => setTooltipOpen(false)}
                      onFocus={() => setTooltipOpen(true)}
                      onBlur={() => setTooltipOpen(false)}
                    >
                      <Icon icon={IconDefinitionConstants.WInfo} />
                    </span>
                  </Tooltip>
                </Grid>
              </Grid>
              <Grid item className={cssExports.monthOrDateRangePickerToggle}>
                <Tooltip
                  title={lockedTooltip}
                  arrow
                  placement="bottom"
                  open={!!modeToggleDisabled && lockedTooltipOpen}
                  classes={{
                    tooltip: cssExports.monthOrDateRangePickerTooltipBubble,
                    arrow: cssExports.monthOrDateRangePickerTooltipArrow,
                    popper: cssExports.monthOrDateRangePickerTooltipPopper,
                  }}
                  PopperProps={{
                    disablePortal: false,
                    modifiers: [{ name: "offset", options: { offset: [0, 9] } }],
                  }}
                >
                  <span
                    onMouseEnter={() => setLockedTooltipOpen(true)}
                    onMouseLeave={() => setLockedTooltipOpen(false)}
                  >
                    <ToggleSwitch
                      name="periodiseWithDate"
                      checked={mode === "date"}
                      onChange={(_, checked) => onToggleChange(checked)}
                      disabled={disabled || modeToggleDisabled}
                      customLabels={{ active: "", inactive: "" }}
                    />
                  </span>
                </Tooltip>
              </Grid>
            </Grid>
          </Box>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

export default MonthOrDateRangePicker;
